/* eslint-disable no-undef */
import Mustache from 'mustache';
import analytics from '../analytics';
import debounce from 'lodash/debounce';

export default class HedinDriveCalculator {
  #element;
  #dom = {};
  #calculationController;
  #mileageController;
  #calculationParameters;

  #financeDetailsTemplate = `
    {{#financeDetails}}
      <li>
        <span class="hedindrive-calculator__finance-title label">{{label}}</span>
        <span class="hedindrive-calculator__finance-value label bold">{{value}}</span>
      </li>
    {{/financeDetails}}
  `;

  constructor(element) {
    this.#element = element;

    this.#findElements();
    this.#bindEvents();
    this.#calculationParameters = JSON.parse( this.#dom.calculationParameters.innerText );

    // Update lease mileages if user is navigating back to the page with browser back button
    window.addEventListener('load', () => {
      this.#fetchAvailableMileages();
    });
  }

  #findElements() {
    this.#dom = {
      availableMileages: this.#element.querySelector('input[name="availableMileages"]'),
      defaultLeaseDuration: this.#element.querySelector('input[name="defaultLeaseDuration"]'),
      leaseDuration: this.#element.querySelectorAll('input[name="leaseDuration"]'),
      leaseMileage: window.multiselects['leaseMileage'],
      hedinDriveColor: window.multiselects['hedinDriveColor'],
      afterMarketAccessories: this.#element.querySelectorAll('input[name="afterMarketAccessories[]"]'),
      factoryAccessories: this.#element.querySelectorAll('input[name="factoryAccessories[]"]'),
      downPayment: this.#element.querySelector('input[name="downPayment"]'),
      totalPrice: document.querySelectorAll('.js-hedindrive-calculator-total-price'),
      financeDetails: document.querySelector('.js-hedindrive-calculator-finance-details'),
      myyja: document.querySelector('input[name="myyja"]'),
      calculationParameters: this.#element.querySelector('.hedindrive-calculation-params'),
      calculatorDisclaimer: document.querySelector('.hedindrive-calculator-request-offer-container > .sidebar-text'),
      infoTextTemplate: document.getElementById('hedindrive-calculation-info-text-template')?.innerHTML,
    };
  }

  #bindEvents() {
    this.#dom.leaseDuration.forEach((input) => {
      input.addEventListener('change', (event) => {
        this.#fetchAvailableMileages();
        const leaseDuration = window.multiselects.leaseMileage.getValue()?.[0];
        this.#pushHedinDriveDuration(event.target.value, leaseDuration);
      });
    });

    this.#dom.leaseMileage.addCallback({
      onValueChange: (data) => {
        if (data.value.length) {
          this.#fetchCalculatorData();
        }

        analytics.pushUIInteractionAnalytics({
          'element': 'input',
          'elementName': `UI - Auto - Vaihda Hedin Drive-sopimuksen kilometrit | ${data.value}000 km`,
          'type': 'click'
        });

        const leaseDuration = this.#element.querySelector('input[name="leaseDuration"]:checked').value;
        this.#pushHedinDriveDuration(leaseDuration, data.value);
      }
    });

    this.#dom.hedinDriveColor?.addCallback({
      onValueChange: (data) => {
        this.#fetchCalculatorData();
      }
    });

    this.#dom.afterMarketAccessories?.forEach((input) => {
      input.addEventListener('change', () => {
        this.#fetchCalculatorData();
      });
    });

    this.#dom.factoryAccessories?.forEach((input) => {
      input.addEventListener('change', () => {
        this.#fetchCalculatorData();
      });
    });

    this.#dom.downPayment.addEventListener('input', debounce((event) => {
      this.#pushDownPaymentAnalyticsEvent(event.target.value);
    }, 2000));//Analytic event for downpayment. Long delay to avoid spamming the event.


    this.#dom.downPayment.addEventListener('input', (event) => {
      this.#fetchCalculatorData();
    });
  }

  #data() {
    const selectedColorDatasets = window.multiselects.hedinDriveColor?.options?.find((option) => option.selected)?.datasets;
    const selectedColorPrice = selectedColorDatasets?.find((color) => color.datasetName === 'price')?.datasetValue || '';

    let possibleFactoryAccessories = Array.from(this.#dom.factoryAccessories)?.map((input) => {
      return {
        id: input.value,
        name: input.dataset.name,
        price: input.dataset.price,
        checked: input.checked,
      };
    });

    let possibleAfterMarketAccessories = Array.from(this.#dom.afterMarketAccessories)?.map((input) => {
      return {
        id: input.value,
        name: input.dataset.name,
        price: input.dataset.price,
        checked: input.checked,
      };
    });

    const selectedAfterMarketAccessories = possibleAfterMarketAccessories.filter((input) => input.checked);
    const selectedFactoryAccessories = possibleFactoryAccessories.filter((input) => input.checked);

    // Remove checked field from selected accessories
    [possibleFactoryAccessories, possibleAfterMarketAccessories, selectedAfterMarketAccessories, selectedFactoryAccessories].forEach((accessories) => {
      accessories.forEach((accessory) => {
        delete accessory.checked;
      });
    });

    let downPayment = Math.round(this.#dom.downPayment.value) || 0;

    if (Math.round(this.#dom.downPayment.value) < 0) {
      // Check min value
      downPayment = 0;
    }

    return {
      leaseDuration: Array.from(this.#dom.leaseDuration).find((input) => input.checked).value,
      leaseMileage: window.multiselects['leaseMileage'].getValue()[0],
      selectedColorPrice,
      selectedAfterMarketAccessories: JSON.stringify(selectedAfterMarketAccessories),
      selectedFactoryAccessories: JSON.stringify(selectedFactoryAccessories),
      ...this.#calculationParameters,
      downPayment,
      possibleFactoryAccessories: JSON.stringify(possibleFactoryAccessories),
      possibleAfterMarketAccessories: JSON.stringify(possibleAfterMarketAccessories),
      myyja: this.#dom.myyja.value,
    };
  }

  #updateMileages(responseData) {
    const updatedLeaseMileages = responseData.availableMileages;
    const selectedMileageOption = this.#dom.leaseMileage.options.find((option) => option.selected);

    this.#dom.leaseMileage.unSelectAll(false);
    this.#dom.leaseMileage.removeOptions(this.#dom.leaseMileage.options.map(o => o.value).filter(Boolean), false);

    const newOptions = updatedLeaseMileages.map(mileage => {
      const tkm = mileage * 1000
      const shownValue = new Intl.NumberFormat('fi-FI').format(tkm) + ' km';

      const selected = shownValue.replace(/[^0-9.]/g, '') === selectedMileageOption?.shownValue.replace(/[^0-9.]/g, '');

      return {
        value: mileage,
        shownValue,
        selected,
      };
    });

    if (newOptions.length > 0 && !newOptions.find((option) => option.selected)){
      newOptions[0].selected = true;
    }

    this.#dom.leaseMileage.addOptions(newOptions);

    this.#fetchCalculatorData();
  }

  #updateFields(responseData) {
    this.#dom.afterMarketAccessories?.forEach((input) => {
      const updatedAccessory = responseData.data.accessoryData.afterMarketAccessories.find((accessory) => accessory.id === input.value);
      input.dataset.price = updatedAccessory.price;
      input.nextElementSibling.querySelector('.js-accessory-price').textContent = `${updatedAccessory.monthlyPrice} €/kk`;
    });

    this.#dom.factoryAccessories?.forEach((input) => {
      const updatedAccessory = responseData.data.accessoryData.factoryAccessories.find((accessory) => accessory.id === input.value);
      input.dataset.price = updatedAccessory.price;
      input.nextElementSibling.querySelector('.js-accessory-price').textContent = `${updatedAccessory.monthlyPrice} €/kk`;
    });

    this.#dom.totalPrice.forEach(priceElement => {
      priceElement.innerText = responseData.data.monthlyPrice < 0 ? '-' : `${responseData.data.monthlyPrice} €/kk`;
    });

    this.#dom.financeDetails.innerHTML = Mustache.render(this.#financeDetailsTemplate, { financeDetails: responseData.data.renderableData });

    if (this.#dom.infoTextTemplate && this.#dom.calculatorDisclaimer) {
      const infoText = Mustache.render(this.#dom.infoTextTemplate, {
        carTruePrice: Number(responseData.data.calculationParams.carTruePrice).toFixed(2).replace('.', ','),
        leaseDuration: responseData.data.calculationParams.arguments[0].leaseDuration,
        leaseMileage: responseData.data.calculationParams.arguments[0].leaseMileage * 1000,
        lastPayment: Number(responseData.data.calculationParams.carResidualValue).toFixed(2).replace('.', ','),
        monthlyPrice: responseData.data.monthlyPrice,
        realYearInterest: responseData.data.calculationParams.realYearlyInterest,
        loanExpenses: responseData.data.calculationParams.loanExpenses,
        totalLoanCost: responseData.data.calculationParams.totalLoanCost,
      });

      this.#dom.calculatorDisclaimer.innerHTML = infoText;
    }
  }

  async #fetchAvailableMileages() {
    if(this.#mileageController) {
      this.#mileageController.abort();
    }

    this.#mileageController = new AbortController();

    try {
      const data = {
        availableMileages: this.#dom.availableMileages.value,
        leaseDuration: Array.from(this.#dom.leaseDuration).find((input) => input.checked).value,
        lClass: this.#calculationParameters.lClass,
      };

      const response = await this.#send('/stage-ajax/api/v1/hedin-drive/available-mileage/', data, this.#mileageController);
      const responseJSON = await response.json();
      this.#updateMileages(responseJSON);
    } catch(error) {
      if(error.name !== 'AbortError') {
        console.warn('Error loading mileage data', error);
        console.log( error.stack );
      }
    }
  }

  async #fetchCalculatorData() {
    if(this.#calculationController) {
      this.#calculationController.abort();
    }

    this.#calculationController = new AbortController();

    try {
      const response = await this.#send('/stage-ajax/api/v1/hedin-drive/calculate/', this.#data(), this.#calculationController);
      const responseJSON = await response.json();
      this.#updateFields(responseJSON);
    } catch(error) {
      if(error.name !== 'AbortError') {
        console.warn('Error loading calculator data', error);
        console.log( error.stack );
      }

    }
  }

  /**
   * Send ajax request via fetch API to the Stage
   * @async
   * @param {String} endpoint API endpoint
   * @param {URLSearchParam} data form data
   * @param {AbortController} controller abort controller signal object
   * @return {Promise} resolved after successful call
   */
  async #send(endpoint, data, controller) {
    const urlParams = new URLSearchParams(data);

    return await fetch(`${endpoint}?${urlParams.toString()}`, {
      method: 'GET',
      headers: { Authorization: 'stage-api-public' },
      signal: controller.signal
    })
  }


  #pushHedinDriveDuration(months, kilometers) {
    analytics.pushUIInteractionAnalytics({
      'element': 'input',
      'elementName': `Hedin Drive - duration | ${months} kk / ${kilometers}000 km`,
      'type': 'click'
    });
  }

  /**
   * Pushes down payment analytics event
   * @param {String} value down payment value
   */
  #pushDownPaymentAnalyticsEvent(value) {
    analytics.pushUIInteractionAnalytics({
      'element': 'input',
      'elementName': `UI - Auto - Lisätty omarahoitus | ${value}€`,
      'type': 'click'
    });
  }

  static init() {
    document.querySelectorAll('.hedindrive-calculator').forEach((form) => {
      new HedinDriveCalculator(form);
    });
  }
}
